<template>
  <Dialog class="we-call-you__dialog" v-model="dialogOpen" scroll-shadow padding large :footer="!showAlert">
    <template>
      <Moles :count="moles" type="info" icon="phone-blue" icon-color="none" position="relative" />
      <div class="we-call-you__content">
        <h2 class="we-call-you__title">¿Necesitas ayuda?</h2>
        <p class="we-call-you__subtitle">Déjanos tu contacto y te llamamos enseguida.</p>
        <Form ref="formulateForm" v-model="form" :fields="schema" externalSubmit @submitForm="handleFormSubmit" />
        <Alert
          v-if="showAlert"
          title="¡Solicitud recibida!"
          subtitle="Nuestro horario de atención es (horario peninsular):"
          :content="isWorkHour ? workTimeContent : scheduleContent"
          showThanks
        />
      </div>
    </template>
    <template #footer>
      <Button btn-style="primary" @click.native="$refs.formulateForm.handleSubmit()">Enviar</Button>
      <Button btn-style="ghost" @click.native="dialogOpen = false">Volver</Button>
    </template>
  </Dialog>
</template>

<script>
import { onMounted, ref, watchEffect } from '@vue/composition-api';
import {
  useNameField,
  useSurnameField,
  useEmailField,
  usePhoneField,
  useSelectField,
  useTermsField,
  useNewsletterField,
} from '@/lego/utils/form/fields';
import { onResize } from '@/lego/utils/window/screen';
import { getTouchedScreen } from '@/lego/utils/tailwind/screen';
import Dialog from '@/lego/components/dialogs/Dialog.vue';
import Moles from '@/lego/components/layout/Moles.vue';
import Alert from '@/lego/components/alerts/Alert.vue';
import Button from '@/lego/components/buttons/Button.vue';
import Form from '@/lego/components/forms/Form.vue';

const OPERATION_OPTIONS = { 0: 'Nueva hipoteca', 1: 'Mejorar mi hipoteca actual' };
const SITUATION_OPTIONS = { 'searching': 'Sigo buscando', 'found': 'Tengo vivienda elegida', 'reservation': 'Tengo arras o reserva' };

export default {
  components: {
    Dialog,
    Moles,
    Button,
    Alert,
    Form,
  },
  setup(props, { root }) {
    const showAlert = ref(false);
    const isWorkHour = ref(false);
    const schema = ref([]);
    const scheduleContent = ['lunes - jueves: 9h a 21h', 'viernes: 9h-20h', 'sábados y domingos: 9h a 18h'];
    const workTimeContent = [
      'En breve te llamaremos para guiarte en tu viaje hipotecario.',
      'Estamos disponibles cuando tú quieras en el 910 054 391.',
    ];
    const breakpoints = { xs: 2, sm: 4 };
    const form = ref(
      root.$inertia.form({
        name: null,
        surname: null,
        email: null,
        phone: null,
        isSubrogation: null,
        situation: null,
        agreement: false,
        isNewsletterSubscriber: false,
      })
    );

    const moles = ref(4);
    const updateMoles = () => {
      const screen = getTouchedScreen(breakpoints);
      moles.value = breakpoints[screen];
    };

    onMounted(() => {
      updateMoles();
    });
    onResize(() => {
      updateMoles();
    });

    const handleFormSubmit = () => {
      root.$inertia.post(route('home.index'), form.value, {
        preserveState: true,
        onSuccess: res => {
          isWorkHour.value = res.props.isWorkHour;
          showAlert.value = true;
        },
      });
    };

    watchEffect(() => {
      schema.value = [
        useNameField({ labelSize: 'md' }),
        useSurnameField({ labelSize: 'md' }),
        useEmailField({ labelSize: 'md' }),
        usePhoneField({ labelSize: 'md' }),
        useSelectField({
          label: '¿Con qué tipo de operación podemos ayudarte?',
          name: 'isSubrogation',
          options: OPERATION_OPTIONS,
          labelSize: 'md',
        }),
        useSelectField({
          label: '¿Cómo llevas la búsqueda de la vivienda?',
          name: 'situation',
          options: SITUATION_OPTIONS,
          hidden: !!parseInt(form.value.isSubrogation),
          labelSize: 'md',
          validation: !!parseInt(form.value.isSubrogation) ? 'optional' : 'required',
        }),
        useTermsField(),
        useNewsletterField(),
      ];
    });

    return {
      dialogOpen: ref(true),
      showAlert,
      isWorkHour,
      scheduleContent,
      workTimeContent,
      moles,
      form,
      handleFormSubmit,
      schema,
    };
  },
};
</script>

<style lang="postcss" scoped>
.we-call-you {
  &__title {
    @apply style-subtitle-bold md:style-title-sm-bold text-center;
  }

  &__subtitle {
    @apply style-body-sm text-neutral-medium mt-nano mb-xxxs text-center;
  }

  &__dialog {
    /deep/.dialog__header {
      @apply p-0 relative;

      .button {
        @apply absolute top-xs right-xs bg-neutral-white rounded-circular p-xxxs z-10 hover:opacity-100 sm:block hidden;
      }
    }

    /deep/.dialog__body__content {
      @apply p-0;
    }

    /deep/.dialog__footer {
      @apply justify-center flex flex-col;

      .button {
        @apply md:max-w-xs max-w-full w-full;

        &:last-child {
          @apply sm:hidden;
        }
      }
    }
  }

  &__content {
    @apply pt-xs md:px-xl px-xxxs pb-0;
  }
}
</style>
